/**
 * Web Push registration and subscription helpers (PWA + home screen).
 */

const SW_PATH = "/sw.js";

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/** iOS and others: installed standalone PWA */
export function isStandaloneDisplay() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export function getVapidPublicKey() {
  const key = process.env.REACT_APP_VAPID_PUBLIC_KEY || "";
  return key.trim();
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) {
    output[i] = raw.charCodeAt(i);
  }
  return output;
}

export async function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  const base = process.env.PUBLIC_URL || "";
  const path = `${base}${SW_PATH}`.replace(/\/+/g, "/");
  return navigator.serviceWorker.register(path);
}

export async function getPushPermission() {
  if (!("Notification" in window)) return "unsupported";
  return Notification.permission;
}

export async function requestPushPermission() {
  if (!("Notification" in window)) {
    throw new Error("Benachrichtigungen werden hier nicht unterstützt.");
  }
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error("Benachrichtigungen sind nicht erlaubt.");
  }
  return permission;
}

/**
 * @returns {Promise<PushSubscription | null>}
 */
export async function subscribeToPush() {
  const vapidPublicKey = getVapidPublicKey();
  if (!vapidPublicKey) {
    throw new Error("Push ist noch nicht konfiguriert (VAPID-Schlüssel fehlt).");
  }

  const registration = await registerServiceWorker();
  if (!registration) {
    throw new Error("Service Worker konnte nicht registriert werden.");
  }

  await requestPushPermission();

  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    });
  }
  return subscription;
}

/** Unsubscribe this browser and return the previous endpoint (for DB cleanup). */
export async function unsubscribePush() {
  const registration = await navigator.serviceWorker.getRegistration();
  const subscription = await registration?.pushManager?.getSubscription();
  if (!subscription) return null;
  const endpoint = subscription.endpoint;
  await subscription.unsubscribe();
  return endpoint;
}

/**
 * @param {PushSubscription} subscription
 */
export function subscriptionToRow(subscription) {
  const json = subscription.toJSON();
  const keys = json.keys || {};
  return {
    endpoint: json.endpoint,
    p256dh: keys.p256dh,
    auth: keys.auth,
  };
}
